/**
 * Client entry point.
 *
 * M0 answered one question here — do client prediction, reconciliation and
 * snapshot interpolation actually work under real network conditions? — with
 * deliberately no content on top, so that a bug had only one possible home.
 * M1 keeps that loop untouched and swaps the featureless test box for a real
 * map: level geometry and prop instances built from shared map data. Teams and
 * rounds arrived later, and later still the level stopped being a constant —
 * `session` below is null until the server says which map this lobby is on.
 */

import {
  Btn,
  DEFAULT_HULL,
  HUNTER_SPEED,
  INTERP_DELAY_MS,
  LOBBY_CODE_ALPHABET,
  LOBBY_CODE_LENGTH,
  MAPS,
  NO_PROP,
  PROP_CATALOGUE,
  TICK_MS,
  canMove,
  eyeHeightFor,
  gateButtons,
  createMoveState,
  isPropEntityId,
  phaseLabel,
  propHull,
  propIndexFromEntityId,
  teamLabel,
  type Hull,
  type MoveConfig,
  type SelfState,
  type StaticWorld,
} from "@prop-hunt/shared";
import { TauntAudio } from "./audio/taunt-audio.js";
import { DebugOverlay } from "./debug/overlay.js";
import { RoundHud } from "./hud/round-hud.js";
import { LobbyHud } from "./hud/lobby-hud.js";
import { secondsToAutoStart } from "./hud/lobby.js";
import { CooldownTracker, TagHud } from "./hud/tag-hud.js";
import { TauntHud } from "./hud/taunt-hud.js";
import { InputSampler } from "./input.js";
import { Connection, type ResolvedLevel } from "./net/connection.js";
import { LocalPredictor } from "./net/prediction.js";
import { conditionsFromUrl } from "./net/transport.js";
import { GameRenderer } from "./render/scene.js";
import { stepSpectatorCamera, type SpectatorPose } from "./spectator-camera.js";

/**
 * Clamp on a single frame's delta.
 *
 * Browsers suspend rAF in background tabs, so returning to a tab hands us a
 * delta measured in seconds. Without this the fixed-timestep loop would try to
 * catch up hundreds of ticks in one frame and fire hundreds of input packets.
 * We drop the missing time instead and let reconciliation snap us back — the
 * server has been simulating us the whole time regardless.
 */
const MAX_FRAME_MS = 250;
const MAX_TICKS_PER_FRAME = 5;
const STATS_WINDOW_MS = 500;

const moveConfig: MoveConfig = { hull: DEFAULT_HULL, maxSpeed: HUNTER_SPEED };

const canvas = document.querySelector<HTMLCanvasElement>("#view")!;
const joinScreen = document.querySelector<HTMLDivElement>("#join")!;
const joinForm = document.querySelector<HTMLFormElement>("#join-form")!;
const nameInput = document.querySelector<HTMLInputElement>("#name-input")!;
const codeInput = document.querySelector<HTMLInputElement>("#code-input")!;
const overlayRoot = document.querySelector<HTMLDivElement>("#overlay-root")!;
const hud = document.querySelector<HTMLDivElement>("#hud")!;
const prompt = document.querySelector<HTMLDivElement>("#prompt")!;
const roundHudRoot = document.querySelector<HTMLDivElement>("#round-hud-root")!;
const lobbyRoot = document.querySelector<HTMLDivElement>("#lobby-root")!;
const createButton = document.querySelector<HTMLButtonElement>("#create-button")!;
const mapSelect = document.querySelector<HTMLSelectElement>("#map-select")!;
const PROMPT_CONTROLS = prompt.textContent ?? "";
/** How long a transient prompt (the mute toggle) displaces the controls line. */
const PROMPT_OVERRIDE_MS = 1200;
let promptOverrideUntil = 0;

const conditions = conditionsFromUrl(location.search);
const input = new InputSampler(canvas);
const overlay = new DebugOverlay(overlayRoot);
const roundHud = new RoundHud(roundHudRoot);
const tagHud = new TagHud(hud, roundHudRoot);
const tauntHud = new TauntHud(roundHudRoot);
const cooldown = new CooldownTracker();
const tauntAudio = new TauntAudio();
// Nothing is predicted here: pressing START asks the server, and the round
// begins when a Msg.Round says it has — the same message everybody else in the
// lobby is waiting on.
const lobbyHud = new LobbyHud(lobbyRoot, () => connection?.sendStart());

/**
 * The level this session is on, and the one renderer built from it.
 *
 * Null until the server's Welcome names the level — which is the whole shape of
 * this change. A lobby is on a map chosen when it was created, so the client
 * cannot know which geometry it needs until it has been told, and building a
 * world from `DEFAULT_MAP` up front would mean drawing and predicting against a
 * level the lobby may not be on.
 *
 * Built exactly once per page, in `startLevel`. A lobby's level is fixed for its
 * life, so there is no swap to worry about: a reconnect to the same lobby comes
 * back to the same level, and `Connection` refuses one that does not.
 */
interface Session {
  level: ResolvedLevel;
  renderer: GameRenderer;
}
let session: Session | null = null;

/**
 * Adopt the level the server says this lobby is on.
 *
 * Called from `Connection`'s `onLevel`, before the first snapshot can arrive, so
 * everything below is looking at the same three things that must never disagree:
 * the collision world prediction steps against, the geometry drawn on screen,
 * and the hash the handshake settled on. They all come from `level`.
 */
function startLevel(level: ResolvedLevel): void {
  if (session !== null) return;
  session = {
    level,
    renderer: new GameRenderer(canvas, level.world, level.map, PROP_CATALOGUE),
  };
  // F4 can be pressed during the handshake, when there is no renderer to tell.
  // Applying it here rather than ignoring it means the toggle is never a
  // keypress that silently did nothing.
  session.renderer.setHullsVisible(hullsVisible);
}

window.addEventListener("resize", () => session?.renderer.resize());

// --- join ------------------------------------------------------------------

// Prefilled only when the address carries one — arriving by invite link should
// be one click, and an empty box is what makes CREATE the obvious action for
// somebody who arrived without one. A random code is still generated on submit
// if the field is empty, so JOIN with nothing in it creates rather than errors.
codeInput.value = lobbyCodeFromUrl() ?? "";
nameInput.value = localStorage.getItem("prop-hunt.name") ?? "";

// Filled from the registry rather than written out in the HTML, so adding a
// level stays a change to maps/index.ts and nowhere else. The default is first
// in `MAPS` and so is the option a page arrives on.
for (const id of MAPS.keys()) {
  const option = document.createElement("option");
  option.value = id;
  option.textContent = id;
  mapSelect.append(option);
}

let connection: Connection | null = null;
let predictor: LocalPredictor | null = null;
/** This client's chosen name, for its own row in the lobby roster. */
let localName = "";
/** The lobby this client is in, for the panel to show and the invite to carry. */
let lobbyCode = "";

// CREATE and JOIN differ by exactly one thing — where the code comes from — so
// CREATE fills the field and lets the one submit path do the rest. It is a
// type="button": the form's single submit button is JOIN, which is what the
// e2e suites click.
createButton.addEventListener("click", () => {
  codeInput.value = randomLobbyCode();
  joinForm.requestSubmit();
});

joinForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const code = normalizeCode(codeInput.value) || randomLobbyCode();
  const name = nameInput.value.trim() || "player";
  localName = name;
  lobbyCode = code;
  localStorage.setItem("prop-hunt.name", name);
  // Put the code in the URL so the whole address is the invite.
  location.hash = code;
  joinScreen.hidden = true;
  hud.hidden = false;
  prompt.hidden = false;
  roundHudRoot.hidden = false;
  // The panel inside decides for itself whether it is on screen; this root is
  // only hidden before there is a lobby at all.
  lobbyRoot.hidden = false;
  input.attach();
  window.addEventListener("keydown", onGlobalKeyDown);
  // Inside the submit handler deliberately: an AudioContext created outside a
  // user gesture starts suspended and is not resumed retroactively, so this is
  // the last moment the game can decide to be able to make a noise at all.
  tauntAudio.enable();
  // The level rides on the URL, and only the first socket into a lobby is heard
  // — see resolveMapId. So this is a request when CREATE made the code, and an
  // ignored preference when JOIN was given one, which is exactly right: the
  // person who made the lobby chose the level.
  //
  // `startLevel` is where the world and the renderer come from, once the server
  // has said which map that is. It is the same world object the predictor steps
  // against, so the solids a possession suppresses are the ones `step` stops
  // querying.
  connection = new Connection(
    websocketUrl(code, mapSelect.value), name, conditions, PROP_CATALOGUE, startLevel,
  );
  connection.connect();
});

/** Whether the F4 collision-hull overlay is on. See `onGlobalKeyDown`. */
let hullsVisible = false;

/**
 * Keys the game answers that are not simulation input.
 *
 * Kept out of `InputSampler` on purpose: that class holds what the player is
 * pressing *this tick* so a frame has exactly one answer, and mute is a
 * setting that survives the frame it was toggled in. It also must not become a
 * button bit — every bit in that field is sent to the server, and the server
 * has no business knowing whether anyone can hear it.
 *
 * F4 belongs here for the same reason F3 lives in the overlay: a prop's mesh
 * may inset from the hull the simulation collides with (a barrel is a cylinder
 * in a box), and `setHullsVisible` is what makes that discrepancy one keypress
 * away instead of something a player discovers by shooting a corner and hitting
 * nothing. The renderer is null until the Welcome names a level, so the flag is
 * held here rather than read back off it.
 */
function onGlobalKeyDown(event: KeyboardEvent): void {
  if (event.repeat) return;

  if (event.code === "KeyM") {
    const muted = tauntAudio.toggleMuted();
    prompt.textContent = muted ? "sound off" : "sound on";
    promptOverrideUntil = performance.now() + PROMPT_OVERRIDE_MS;
    return;
  }

  if (event.code === "F4") {
    // F4 is Chrome's caret-browsing toggle on some platforms and closes a
    // window on others; nobody pressing it here means either.
    event.preventDefault();
    hullsVisible = !hullsVisible;
    session?.renderer.setHullsVisible(hullsVisible);
    prompt.textContent = hullsVisible ? "collision hulls on" : "collision hulls off";
    promptOverrideUntil = performance.now() + PROMPT_OVERRIDE_MS;
  }
}

function lobbyCodeFromUrl(): string | null {
  const fromHash = normalizeCode(location.hash.slice(1));
  if (fromHash.length === LOBBY_CODE_LENGTH) return fromHash;
  const fromQuery = normalizeCode(new URLSearchParams(location.search).get("lobby") ?? "");
  return fromQuery.length === LOBBY_CODE_LENGTH ? fromQuery : null;
}

function normalizeCode(raw: string): string {
  return raw
    .toUpperCase()
    .split("")
    .filter((ch) => LOBBY_CODE_ALPHABET.includes(ch))
    .join("")
    .slice(0, LOBBY_CODE_LENGTH);
}

function randomLobbyCode(): string {
  const bytes = new Uint8Array(LOBBY_CODE_LENGTH);
  crypto.getRandomValues(bytes);
  let code = "";
  for (const byte of bytes) {
    code += LOBBY_CODE_ALPHABET[byte % LOBBY_CODE_ALPHABET.length];
  }
  return code;
}

/**
 * Origin the lobby server answers on, or "" for "the same origin as this page".
 *
 * Empty is the dev and Cloudflare-only case: Vite proxies /api to wrangler, and
 * a Worker deploy serves the client itself, so a relative URL is correct in
 * both. It is set when the client is hosted somewhere that cannot carry a
 * WebSocket upgrade to the Worker — Firebase Hosting is a static CDN and rejects
 * the upgrade — in which case the socket has to address the Worker directly.
 */
const SERVER_ORIGIN = import.meta.env.VITE_SERVER_ORIGIN ?? "";

function websocketUrl(code: string, mapId: string): string {
  const url = new URL(`/api/lobby/${code}/ws`, SERVER_ORIGIN || location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // Only honoured on the first socket into a lobby; after that the lobby has a
  // level and says so in the Welcome. See LobbyDurableObject.fetch.
  if (mapId !== "") url.searchParams.set("map", mapId);
  return url.toString();
}

// --- loop ------------------------------------------------------------------

const renderPose = { x: 0, y: 0, z: 0 };

/**
 * Where the free-fly camera is, while the local player is spectating.
 *
 * Lives outside `frame()` so it persists across frames without a class of its
 * own — the same reason `renderPose` does. Seeded from `renderPose` at the
 * moment `amFound` turns true (see `frame`), so the view does not jump the
 * instant a shot lands; from then on `stepSpectatorCamera` owns it entirely,
 * and `renderPose`/the predictor keep running underneath but stop being what
 * the camera reads.
 */
let spectatorPose: SpectatorPose = { x: 0, y: 0, z: 0 };
/** Edge-detects the Hiding/Hunting → found transition, to seed the above once. */
let wasSpectating = false;

/**
 * The session epoch this loop has already caught up with. See `Connection`.
 *
 * Compared rather than consumed, so a reconnect that completes between two
 * frames cannot be missed.
 */
let sessionEpoch = 0;

let lastFrameAt = performance.now();
let accumulator = 0;
let fps = 60;

let statsAt = lastFrameAt;
let bytesUpAt = 0;
let bytesDownAt = 0;
let snapshotsAt = 0;
let kbUp = 0;
let kbDown = 0;
let snapshotsPerSecond = 0;

requestAnimationFrame(frame);

function frame(now: number): void {
  requestAnimationFrame(frame);

  const dtMs = Math.min(now - lastFrameAt, MAX_FRAME_MS);
  lastFrameAt = now;
  fps = fps * 0.9 + (1000 / Math.max(dtMs, 1)) * 0.1;

  let interpDepth = 0;
  let starvedEntities = 0;

  // Narrowed once rather than re-checked at every use. Null while a handshake is
  // in flight: the loop still runs, because the lobby panel has something to say
  // about connecting, there is simply no geometry to simulate or draw yet.
  const active = session;

  if (connection !== null) {
    if (connection.sessionEpoch !== sessionEpoch) {
      sessionEpoch = connection.sessionEpoch;
      onSessionStart();
    }

    if (connection.status === "open" && active !== null) {
      connection.maybePing(now);
      applyAuthoritativeState(connection, active.level);
      runFixedSteps(connection, dtMs, now, active.level.world);
    }

    if (connection.clock.ready && active !== null) {
      const renderTime = connection.renderTime(now, INTERP_DELAY_MS);
      // Avatars are for player-identity entities only. Every prop — worn or
      // not, see the M3 possession note in lobby-logic.ts — arrives under a
      // propEntityId and is routed to setPropPose below instead, so it never
      // becomes an avatar and never costs its own draw call.
      const avatarIds: number[] = [];
      for (const id of connection.timelines.keys()) {
        if (!isPropEntityId(id)) avatarIds.push(id);
      }
      active.renderer.syncAvatars(avatarIds);
      for (const [id, timeline] of connection.timelines) {
        interpDepth += timeline.depth;
        const pose = timeline.sample(renderTime);
        if (pose === null) continue;
        if (pose.starved) starvedEntities += 1;
        if (isPropEntityId(id)) {
          active.renderer.setPropPose(propIndexFromEntityId(id), pose);
        } else {
          active.renderer.setAvatarPose(id, {
            x: pose.x,
            y: pose.y,
            z: pose.z,
            yaw: pose.yaw,
            // 0 means an undisguised player. Anything else is the catalogue
            // id a hider's own team sees them wearing, and the renderer
            // sizes the box from the same hull the simulation collides with.
            propId: pose.propId,
            starved: pose.starved,
          });
        }
        timeline.prune(renderTime);
      }
    }

    updateBandwidth(connection, now);
  }

  predictor?.decayCorrection(dtMs);

  const alpha = accumulator / TICK_MS;
  if (predictor !== null) {
    predictor.renderPose(alpha, renderPose);
  }

  // A found hider's body is frozen (canMove in round.ts) but still simulated
  // and still feeding renderPose above — riding it would leave the camera
  // staring at one fixed point for the rest of the round. Once found, the
  // camera detaches and flies under direct input instead; see
  // spectator-camera.ts for why that is safe to do without touching the
  // network path at all.
  const spectating = connection?.amFound ?? false;
  if (spectating && !wasSpectating) {
    spectatorPose = { x: renderPose.x, y: renderPose.y, z: renderPose.z };
  }
  wasSpectating = spectating;
  if (spectating) {
    spectatorPose = stepSpectatorCamera(spectatorPose, input.buttons, input.yaw, dtMs);
  }

  const camPose = spectating ? spectatorPose : renderPose;
  // Crouch is repurposed as "descend" while flying; there is no hull to
  // shrink for a camera that was never simulated.
  const crouching = spectating ? false : (input.buttons & Btn.Crouch) !== 0;
  // Nothing to draw before the Welcome named a level. The canvas keeps its clear
  // colour for the length of a handshake, which is the honest picture: this
  // client does not yet know which room it is standing in.
  active?.renderer.render(camPose.x, camPose.y, camPose.z, input.yaw, input.pitch, crouching);
  // The ears go exactly where the camera went — `render` adds the same
  // `eyeHeightFor` to the same feet position, so a taunt is heard from the eye
  // it is seen from rather than from the floor half a body below it.
  tauntAudio.setListener(
    camPose.x, camPose.y + eyeHeightFor(crouching), camPose.z,
    input.yaw, input.pitch,
  );

  updatePrompt(now);

  if (connection !== null) {
    // Ticks rather than milliseconds, because the cooldown is counted in ticks
    // on the server. Falls back to the last snapshot's wrapped tick only before
    // the clock is ready, where nothing can be shot at anyway.
    const tickNow = connection.estimatedServerTick(now) ?? 0;
    cooldown.onTag(connection.lastTag, connection.localId, tickNow);
    tagHud.onTag(connection.lastTag, connection.localId, nameOf, now);
    // Both dedupe against the latch by identity, so handing them the same
    // message every frame plays and shows it exactly once.
    tauntAudio.onTaunt(connection.lastTaunt);
    tauntHud.onTaunt(connection.lastTaunt, now);
    tagHud.update(
      {
        phase: connection.gatePhase(now),
        myTeam: connection.myTeam,
        amFound: connection.amFound,
        cooldownTicksLeft: cooldown.ticksLeft(tickNow),
        cooldownTotalTicks: cooldown.total,
      },
      now,
    );

    // The lobby is a Warmup thing, so it reads the same derived phase the
    // movement gate does rather than the one the newest Msg.Round carried —
    // otherwise the panel would sit over the start of the round for ~RTT/2.
    lobbyHud.update(
      {
        phase: connection.gatePhase(now),
        // A Welcome has landed, which is what makes `localId` and the roster
        // mean anything. Until then the panel says it is connecting rather
        // than counting itself as a lobby of one.
        connected: connection.status === "open" && connection.localId !== 0,
        // peers excludes us, so this is everyone. It tracks PeerJoin/PeerLeave
        // rather than the Msg.Round roster, which is only resent on a change
        // the server thinks is worth a broadcast.
        playerCount: connection.peers.size + 1,
        amHost: connection.amHost,
        hostName: connection.hostId === 0 ? null : nameOf(connection.hostId),
        // `tickNow` is 0 until the clock has a sample, which would read as a
        // countdown of several hours. No countdown at all is the honest answer
        // for the second or so before the first pong lands.
        secondsToAutoStart: connection.clock.ready
          ? secondsToAutoStart(connection.startsAtTick, tickNow)
          : null,
        // From the connection rather than from the picker on the join screen:
        // the picker is what this client *asked* for, and only the first socket
        // into a lobby is heard. What the panel must show is what the lobby
        // actually is, which is the Welcome's answer.
        levelId: connection.levelId,
        localId: connection.localId,
        localName,
        peers: connection.peers,
        hostId: connection.hostId,
        code: lobbyCode,
      },
      now,
    );

    roundHud.update({
      // Both derived from the clock rather than read off the newest Msg.Round,
      // and both must be: `phaseStartedTick` is a u32 now, so subtracting it
      // from the snapshot's wrapped u16 tick — which is what `serverTick` is —
      // would put the countdown out by up to a full wrap.
      round: connection.roundNow(now),
      serverTick: connection.estimatedServerTick(now) ?? connection.round.phaseStartedTick,
      myTeam: connection.myTeam,
      teams: connection.teams,
      found: connection.found,
      amFound: connection.amFound,
    });
  }

  tauntHud.update(now);

  overlay.update({
    status: connection?.status ?? "idle",
    detail: connection?.detail ?? "",
    fps,
    // Read after `renderer.render` above, which is what resets the counters —
    // so these describe the frame just drawn.
    drawCalls: active?.renderer.drawCalls ?? 0,
    triangles: active?.renderer.triangles ?? 0,
    rttMs: connection?.clock.rtt ?? 0,
    clockOffsetMs: connection?.clock.offset ?? 0,
    clockSamples: connection?.clock.samples ?? 0,
    serverTick: connection?.serverTick ?? 0,
    // The phase the gate is actually using this frame, not the one the last
    // Msg.Round carried — otherwise the overlay would report the very
    // disagreement the gate no longer has.
    gatePhase: connection === null ? "-" : phaseLabel(connection.gatePhase(now)),
    myTeam: connection === null ? "-" : teamLabel(connection.myTeam),
    snapshotsPerSecond,
    predictionErrorM: predictor?.lastError ?? 0,
    pendingInputs: predictor?.pendingCount ?? 0,
    lastReplayCount: predictor?.lastReplayCount ?? 0,
    snapCount: predictor?.snapCount ?? 0,
    correctionCount: predictor?.correctionCount ?? 0,
    correctionM: predictor?.correctionMagnitude ?? 0,
    interpBufferDepth: interpDepth,
    starvedEntities,
    entityCount: connection?.timelines.size ?? 0,
    kbPerSecondUp: kbUp,
    kbPerSecondDown: kbDown,
    conditions: describeConditions(),
    posX: renderPose.x,
    posY: renderPose.y,
    posZ: renderPose.z,
  });
}

/**
 * Drop everything this module predicted or accumulated under the previous
 * identity, because a new one has just been issued.
 *
 * Runs on the first join too, where it is a no-op, and on every reconnect,
 * where it is not: the server has spawned us afresh under a new player id, so
 * a predictor still holding the position we were dropped at would reconcile
 * from there to the spawn point as if it were a prediction error — the kind of
 * correction the whole reconciliation design exists to make rare, arriving for
 * a reason that is not a mistake. Nulling it makes the next snapshot seed it
 * outright, which is the same thing `applyAuthoritativeState` does on a cold
 * start.
 *
 * The hull goes back to the default for the same reason: possession is a
 * property of a session, and the new one starts undisguised.
 */
function onSessionStart(): void {
  predictor = null;
  moveConfig.hull = DEFAULT_HULL;
  accumulator = 0;
  // A found hider who reconnects is not found any more, so the camera has to
  // be handed back to the body it will be re-seeded on.
  wasSpectating = false;
}

/**
 * A player's name for the HUD, or a placeholder.
 *
 * A found hider's name is public — they have stopped being hidden — so this
 * needs no view split of its own. Falls back to the id for a player whose
 * PeerJoin has not landed, which is preferable to a message that reads as
 * though nobody was found.
 */
function nameOf(id: number): string {
  return connection?.peers.get(id) ?? (id === connection?.localId ? "you" : `player ${id}`);
}

/**
 * The prompt doubles as the one piece of failure UI.
 *
 * Every rejection arrives through the same path with a `detail` string, so
 * this needs no per-reason handling: a stale build refused for
 * RejectReason.MapMismatch reads the same way a protocol mismatch does. The
 * debug overlay already carries `detail`, but it is F3-hideable and reads as
 * diagnostics — without this, being refused looks identical to a game that
 * failed to start for no reason at all, which is the exact confusion the
 * handshake check exists to prevent.
 */
function updatePrompt(now: number): void {
  const status = connection?.status;
  // "reconnecting" is here rather than treated as a transient because it is
  // the one the player most needs to see: without it a retry that is working
  // is indistinguishable from a frozen game, and they reload the page — which
  // costs them their lobby, mid-retry, for no reason.
  if (status === "rejected" || status === "closed" || status === "reconnecting") {
    prompt.textContent =
      connection?.detail || (status === "rejected" ? "connection refused" : "disconnected");
    prompt.hidden = false;
    return;
  }
  // Below a failure, above the controls line: the mute state is worth a moment
  // of the player's attention and nothing more. It stays visible under pointer
  // lock, where the controls line deliberately does not — the whole point of
  // the toggle is that it is used mid-round.
  if (now < promptOverrideUntil) {
    prompt.hidden = false;
    return;
  }
  prompt.textContent = PROMPT_CONTROLS;
  prompt.hidden = connection === null || input.pointerLocked;
}

/**
 * Fold in the newest snapshot before simulating this frame, so the ticks below
 * start from the server's truth rather than extending a stale guess.
 */
function applyAuthoritativeState(conn: Connection, level: ResolvedLevel): void {
  if (!conn.hasNewAuthoritative || conn.latestSelf === null) return;
  conn.hasNewAuthoritative = false;

  if (predictor === null) {
    // Seed from the server's spawn rather than starting at the origin and
    // letting the first reconcile snap us across the map.
    predictor = new LocalPredictor(seedState(conn.latestSelf));
    return;
  }

  if (conn.propChanged) {
    conn.propChanged = false;
    // The server has changed our shape. Adopt the new hull and take its state
    // whole rather than reconciling into it: every unacknowledged input was
    // recorded while we were a different size, and replaying them through the
    // new hull resolves collisions that never happened. `adopt` is the same
    // move `reconcile` makes past RECONCILE_SNAP_DISTANCE, for the same reason
    // — some corrections are not worth easing into.
    moveConfig.hull = hullFor(level, conn.selfPropIndex);
    predictor.adopt(conn.latestSelf);
    return;
  }

  predictor.reconcile(conn.latestSelf, conn.latestAck, moveConfig, level.world);
}

/** The hull that goes with a possessed prop, or the player's own. */
function hullFor(level: ResolvedLevel, propIndex: number): Hull {
  if (propIndex === NO_PROP) return DEFAULT_HULL;
  return propHull(level.map, PROP_CATALOGUE, propIndex) ?? DEFAULT_HULL;
}

function seedState(self: SelfState) {
  const state = createMoveState(self.x, self.y, self.z);
  state.vx = self.vx;
  state.vy = self.vy;
  state.vz = self.vz;
  state.onGround = self.onGround;
  state.yaw = input.yaw;
  state.pitch = input.pitch;
  return state;
}

/**
 * Fixed 20Hz simulation, decoupled from the render rate.
 *
 * The step must match the server's exactly, so the rate is not negotiable.
 * Smoothness at higher frame rates comes from blending between the previous
 * and current predicted states at render time instead.
 */
function runFixedSteps(conn: Connection, dtMs: number, now: number, world: StaticWorld): void {
  accumulator += dtMs;
  let ticks = 0;
  // Derived from the clock rather than read off the newest Msg.Round, so the
  // hunter freeze lifts on the same tick here as on the server instead of
  // ~RTT/2 later. Hoisted out of the loop: every tick in one frame is gated on
  // the same estimate, and `now` does not advance inside it.
  const phase = conn.gatePhase(now);
  while (accumulator >= TICK_MS && ticks < MAX_TICKS_PER_FRAME) {
    accumulator -= TICK_MS;
    ticks += 1;
    // Gate before sending, not just before predicting, so the frame the server
    // simulates is byte-for-byte the frame we predicted with. The server gates
    // too — it cannot trust us — but if we predicted ungated movement while it
    // held us still, a frozen hunter would disagree by a full tick of movement
    // every tick until the error passed RECONCILE_SNAP_DISTANCE and snapped.
    // `sampleTick` rather than `buttons`: it also carries any Fire/Use/Jump
    // that was pressed and released between two ticks, which is a real click on
    // a slow frame and nothing at all to a sampler that only reads what is
    // still down. It consumes the latch, so this must stay the only caller.
    const buttons = gateButtons(input.sampleTick(), canMove(phase, conn.myTeam, conn.amFound));
    const frameInput = conn.sendInput(buttons, input.yaw, input.pitch, now);
    predictor?.predict(frameInput, moveConfig, world);
  }
  // Fell too far behind to catch up honestly — drop the backlog rather than
  // spiralling.
  if (ticks >= MAX_TICKS_PER_FRAME) accumulator = 0;
}

function updateBandwidth(conn: Connection, now: number): void {
  const elapsed = now - statsAt;
  if (elapsed < STATS_WINDOW_MS) return;
  const seconds = elapsed / 1000;
  kbUp = (conn.bytesSent - bytesUpAt) / 1024 / seconds;
  kbDown = (conn.bytesReceived - bytesDownAt) / 1024 / seconds;
  snapshotsPerSecond = (conn.snapshotsReceived - snapshotsAt) / seconds;
  bytesUpAt = conn.bytesSent;
  bytesDownAt = conn.bytesReceived;
  snapshotsAt = conn.snapshotsReceived;
  statsAt = now;
}

function describeConditions(): string {
  if (conditions.latencyMs === 0 && conditions.jitterMs === 0 && conditions.lossPercent === 0) {
    return "no simulated latency (add ?lag=150&jitter=50)";
  }
  return `lag ${conditions.latencyMs}ms +/-${conditions.jitterMs}ms, loss ${conditions.lossPercent}%`;
}
